import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import Navbar from "../../Header/header";
import SASidebar from "../../Dashboard/SuperAdmin/Superadmincom/SuperAdminSidebar";
import { API_LoginURL } from "../../../config";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { RotatingLines } from "react-loader-spinner";
import "./CourseMaster.css";

export default function CourseMasterCreation() {
  // State management
  const [course, setCourse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [imageSizeError, setImageSizeError] = useState("");
  const [formData, setFormData] = useState({
    courseId: 0,
    courseCode: "",
    courseName: "",
    courseDescription: "",
    courseStatus: true,
    aboutUs: "",
    whatWillLearn: "",
    materialInclude: "",
    CourseDifficulty: "Easy",
    CourseDuration: "",
    AuthorName: "",
    CouseThumbnailImageFile: null,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const itemsPerPage = 10;

  // Refs for Quill editors
  const aboutUsRef = useRef(null);
  const aboutUsQuillRef = useRef(null);
  const whatWillLearnRef = useRef(null);
  const whatWillLearnQuillRef = useRef(null);
  const materialIncludeRef = useRef(null);
  const materialIncludeQuillRef = useRef(null);

  // Quill editor configuration
  const toolbarOptions = [
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    [{ 'header': 1 }, { 'header': 2 }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'script': 'sub' }, { 'script': 'super' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    [{ 'direction': 'ltr' }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'font': [] }],
    [{ 'align': [] }],
    ['clean'],
    ['link', 'image', 'video']
  ];

  // Initialize Quill editors
  const initializeQuillEditors = () => {
    const initEditor = (ref, quillRef, placeholder, formField) => {
      if (ref.current && !quillRef.current) {
        quillRef.current = new Quill(ref.current, {
          theme: "snow",
          placeholder,
          modules: { 
            toolbar: toolbarOptions,
          },
        });
        
        // Explicitly set direction to LTR
        quillRef.current.root.style.direction = 'ltr';
        
        // Set initial content if it exists in formData
        if (formData[formField]) {
          quillRef.current.root.innerHTML = formData[formField];
        }
        
        quillRef.current.on("text-change", () => {
          setFormData((prev) => ({
            ...prev,
            [formField]: quillRef.current.root.innerHTML,
          }));
        });
      }
    };

    initEditor(
      aboutUsRef,
      aboutUsQuillRef,
      "Enter About Us content...",
      "aboutUs"
    );
    initEditor(
      whatWillLearnRef,
      whatWillLearnQuillRef,
      "Enter what you will learn...",
      "whatWillLearn"
    );
    initEditor(
      materialIncludeRef,
      materialIncludeQuillRef,
      "Enter included materials...",
      "materialInclude"
    );
  };

  // Load data and initialize editors
  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchCourse();
        initializeQuillEditors();
      } catch (error) {
        console.error("Initialization error:", error);
      }
    };
    fetchData();
  }, []);

  // Handle modal show events to update Quill editors with content
  useEffect(() => {
    const modal = document.getElementById('courseModal');
    if (!modal) return;

    const handleShow = () => {
      // Update editors with current form data when modal is shown
      if (aboutUsQuillRef.current && formData.aboutUs) {
        aboutUsQuillRef.current.root.innerHTML = formData.aboutUs;
      }
      if (whatWillLearnQuillRef.current && formData.whatWillLearn) {
        whatWillLearnQuillRef.current.root.innerHTML = formData.whatWillLearn;
      }
      if (materialIncludeQuillRef.current && formData.materialInclude) {
        materialIncludeQuillRef.current.root.innerHTML = formData.materialInclude;
      }
    };

    modal.addEventListener('shown.bs.modal', handleShow);

    return () => {
      modal.removeEventListener('shown.bs.modal', handleShow);
    };
  }, [formData.aboutUs, formData.whatWillLearn, formData.materialInclude]);

  // API calls
  const fetchCourse = async () => {
    try {
      const response = await axios.get(
        `${API_LoginURL}Course/SuperAdminCourseMasterList`,
        {
          headers: getHeaders(),
        }
      );
      setCourse(response.data);
    } catch (error) {
      console.error("Error fetching course:", error);
      Swal.fire("Error", "Failed to load courses", "error");
    } finally {
      setLoading(false);
    }
  };

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: localStorage.getItem("token"),
  });

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedValue = name === "courseStatus" ? value === "true" : value;
    setFormData({ ...formData, [name]: updatedValue });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setImageSizeError("Image size should be less than 2MB");
        return;
      }
      
      // Check image dimensions
      const img = new Image();
      img.onload = function() {
        const width = this.width;
        const height = this.height;
        const aspectRatio = width / height;
        const targetAspectRatio = 16 / 9;
        const aspectRatioTolerance = 0.1; // 10% tolerance
        
        if (Math.abs(aspectRatio - targetAspectRatio) > aspectRatioTolerance) {
          setImageSizeError("Recommended aspect ratio is 16:9 (800x450px)");
        } else {
          setImageSizeError("");
        }
      };
      img.src = URL.createObjectURL(file);
      
      setFormData({ ...formData, CouseThumbnailImageFile: file });
      setThumbnailUrl(URL.createObjectURL(file));
    } else {
      setImageSizeError("");
      setFormData({ ...formData, CouseThumbnailImageFile: null });
      setThumbnailUrl(null);
    }
  };

  const resetForm = () => {
    setFormData({
      courseId: 0,
      courseCode: "",
      courseName: "",
      courseDescription: "",
      courseStatus: true,
      aboutUs: "",
      whatWillLearn: "",
      materialInclude: "",
      CourseDifficulty: "Easy",
      CourseDuration: "",
      AuthorName: "",
      CouseThumbnailImageFile: null,
    });
    setThumbnailUrl(null);
    setFormSubmitted(false);
    setImageSizeError("");

    // Clear Quill editors
    if (aboutUsQuillRef.current) aboutUsQuillRef.current.root.innerHTML = "";
    if (whatWillLearnQuillRef.current)
      whatWillLearnQuillRef.current.root.innerHTML = "";
    if (materialIncludeQuillRef.current)
      materialIncludeQuillRef.current.root.innerHTML = "";
  };

  const handleEdit = (cours) => {
    setFormData({
      courseId: cours.courseId || 0,
      courseCode: cours.courseCode || "",
      courseName: cours.courseName || "",
      courseDescription: cours.courseDescription || "",
      courseStatus: cours.courseStatus,
      aboutUs: cours.aboutUs || "",
      whatWillLearn: cours.whatYouWillLearn || "",
      materialInclude: cours.materialInclude || "",
      CourseDifficulty: cours.courseDifficulty || "Easy",
      CourseDuration: cours.courseDuration || "",
      AuthorName: cours.authorName || "",
      CouseThumbnailImageFile: null,
    });
    setThumbnailUrl(cours.thumbnailUrl || null);
    setImageSizeError("");

    // Update Quill editors with existing content after a short delay
    // This ensures the modal is fully opened and editors are ready
    setTimeout(() => {
      if (aboutUsQuillRef.current && cours.aboutUs) {
        aboutUsQuillRef.current.root.innerHTML = cours.aboutUs;
      }
      if (whatWillLearnQuillRef.current && cours.whatYouWillLearn) {
        whatWillLearnQuillRef.current.root.innerHTML = cours.whatYouWillLearn;
      }
      if (materialIncludeQuillRef.current && cours.materialInclude) {
        materialIncludeQuillRef.current.root.innerHTML = cours.materialInclude;
      }
    }, 300);
  };

  const handleDelete = async (courseId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;
    try {
      await axios.post(
        `${API_LoginURL}Course/DeleteCourseMaster`,
        { courseId:courseId },
        { headers: getHeaders() }
      );
      Swal.fire("Deleted!", "Course has been deleted.", "success");
    } catch (error) {
      console.error("Error deleting course:", error);
      Swal.fire("Error", "Failed to delete course", "error");
    }
    setCourse(course.filter((c) => c.courseId !== courseId));
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    if (!formData.courseCode.trim())
      errors.courseCode = "Course code is required";
    if (!formData.courseName.trim())
      errors.courseName = "Course name is required";
    if (imageSizeError) 
      errors.image = imageSizeError;
    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  // Form submission
  const handleFormSubmit = async () => {
    setFormSubmitted(true);
    const { isValid } = validateForm();
    if (!isValid) return;

    const formDataToSend = new FormData();
    
    // Get updated content from Quill editors
    const updatedFormData = {
      ...formData,
      aboutUs: aboutUsQuillRef.current?.root.innerHTML || formData.aboutUs,
      whatWillLearn: whatWillLearnQuillRef.current?.root.innerHTML || formData.whatWillLearn,
      materialInclude: materialIncludeQuillRef.current?.root.innerHTML || formData.materialInclude,
    };

    Object.entries(updatedFormData).forEach(([key, value]) => {
      if (key !== "CouseThumbnailImageFile" || value) {
        formDataToSend.append(key, value);
      }
    });

    try {
      const response = await axios.post(
        `${API_LoginURL}Course/CourseMasterAddEdit`,
        formDataToSend,
        { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } }
      );

      if (response.data) {
        Swal.fire("Success!", "Course saved successfully", "success");
        fetchCourse();
        // Close the modal properly
        const closeButton = document.querySelector('#courseModal .btn-close');
        if (closeButton) {
          closeButton.click();
        }
        resetForm();
      }
    } catch (error) {
      console.error("Error saving course:", error);
      Swal.fire("Error", "Failed to save course", "error");
    }
  };

  // Search and pagination
  const filteredCourses = course.filter((c) => {
    const matchesSearch =
      c.courseName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.courseCode.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "active" && c.courseStatus) ||
      (filterStatus === "inactive" && !c.courseStatus);
    return matchesSearch && matchesStatus;
  });

  const pageCount = Math.ceil(filteredCourses.length / itemsPerPage);
  const displayedCourses = filteredCourses.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  // Helper function for difficulty badge color
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "Easy":
        return "";
      case "Medium":
        return "warning";
      case "Hard":
        return "danger";
      default:
        return "primary";
    }
  };

  return (
    <div className="container-scroller">
      <Navbar />
      <div className="container-fluid page-body-wrapper">
        <SASidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="col-lg-12 grid-margin stretch-card">
              <div className="card">
                <div className="card-body">
                  {/* Header Section */}
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-3 mb-md-0">Course Master</h4>

                    {/* Search and Filter */}
                    <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100 w-md-auto">
                      {/* Search */}
                      <div
                        className="flex-grow-1 me-md-2 position-relative"
                        style={{ maxWidth: "500px" }}
                      >
                        <input
                          type="text"
                          className="form-control form-control-sm ps-5 rounded-pill border-0"
                          placeholder="Search by Name"
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(0);
                          }}
                          style={{
                            height: "38px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            transition: "all 0.3s ease",
                            backgroundColor: "#f8f9fa",
                          }}
                          onFocus={(e) => {
                            e.target.style.boxShadow =
                              "0 2px 12px rgba(0,0,0,0.15)";
                            e.target.style.backgroundColor = "#fff";
                          }}
                          onBlur={(e) => {
                            e.target.style.boxShadow =
                              "0 2px 8px rgba(0,0,0,0.08)";
                            e.target.style.backgroundColor = "#f8f9fa";
                          }}
                        />
                        <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>

                        {/* Optional clear button when text is entered */}
                        {searchQuery && (
                          <button
                            className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-2 p-0 text-muted"
                            onClick={() => setSearchQuery("")}
                            style={{
                              border: "none",
                              background: "transparent",
                              fontSize: "12px",
                            }}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        )}
                      </div>

                      {/* Status Filter */}
                      <div className="d-flex align-items-center">
                        <i className="fas fa-filter me-2 text-muted"></i>
                        <select
                          className="form-select"
                          value={filterStatus}
                          onChange={(e) => {
                            setFilterStatus(e.target.value);
                            setCurrentPage(0);
                          }}
                          style={{ minWidth: "120px" }}
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>

                      {/* Add Course Button */}
                      <button
                        className="btn btn-primary d-flex align-items-center"
                        data-bs-toggle="modal"
                        data-bs-target="#courseModal"
                        onClick={resetForm}
                      >
                        <i className="fas fa-plus me-2"></i>
                        <span className="d-none d-md-inline">Add Course</span>
                      </button>
                    </div>
                  </div>

                  {/* Course Table */}
                  <div className="table-responsive">
                    {loading ? (
                      <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ height: "200px" }}
                      >
                        <RotatingLines
                          strokeColor="#4e73df"
                          strokeWidth="5"
                          animationDuration="0.75"
                          width="50"
                        />
                        <p className="mt-3 ms-3">Loading courses...</p>
                      </div>
                    ) : (
                      <>
                        <table className="table table-hover">
                          <thead className="table-light">
                            <tr>
                              <th width="10%">ID</th>
                              <th width="15%">Code</th>
                              <th width="30%">Course Name</th>
                              <th width="15%">Status</th>
                              <th width="15%">Difficulty</th>
                              <th width="15%" className="text-end">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedCourses.length > 0 ? (
                              displayedCourses.map((cours) => (
                                <tr key={cours.courseId}>
                                  <td>{cours.courseId}</td>
                                  <td>
                                    <span>{cours.courseCode}</span>
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      {cours.thumbnailUrl && (
                                        <img
                                          src={cours.thumbnailUrl}
                                          alt="Course thumbnail"
                                          className="rounded me-3"
                                          style={{
                                            width: "40px",
                                            height: "40px",
                                            objectFit: "cover",
                                          }}
                                        />
                                      )}
                                      <div>
                                        <h6 className="mb-0">
                                          {cours.courseName}
                                        </h6>
                                        <small
                                          className="text-muted text-truncate d-block"
                                          style={{ maxWidth: "200px" }}
                                        >
                                          {cours.courseDescription?.substring(
                                            0,
                                            50
                                          )}
                                          ...
                                        </small>
                                      </div>
                                    </div>
                                  </td>
                                  <td
                                    style={{
                                      color: cours.courseStatus
                                        ? "green"
                                        : "red",
                                    }}
                                  >
                                    {cours.courseStatus ? "Active" : "Inactive"}
                                  </td>

                                  <td
                                    style={{
                                      color:
                                        cours.courseDifficulty == "Easy"
                                          ? "green"
                                          : cours.courseDifficulty == "Medium"
                                          ? "orange"
                                          : cours.courseDifficulty == "Hard"
                                          ? "red"
                                          : "",
                                    }}
                                  >
                                    {cours.courseDifficulty == "Easy"
                                      ? "Easy"
                                      : cours.courseDifficulty == "Medium"
                                      ? "Medium"
                                      : cours.courseDifficulty == "Hard"
                                      ? "Hard"
                                      : ""}
                                  </td>
                                  <td className="text-end">
                                    <div className="d-flex justify-content-end gap-2">
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        data-bs-toggle="modal"
                                        data-bs-target="#courseModal"
                                        onClick={() => handleEdit(cours)}
                                        title="Edit"
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() =>
                                          handleDelete(cours.courseId)
                                        }
                                        title="Delete"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center py-4">
                                  <div className="d-flex flex-column align-items-center">
                                    <img
                                      src="https://img.icons8.com/ios/50/000000/no-camera--v1.png"
                                      alt="No courses"
                                      style={{ opacity: 0.5, width: "60px" }}
                                    />
                                    <p className="mt-3 text-muted">
                                      No courses found
                                    </p>
                                    <button
                                      className="btn btn-sm btn-primary mt-2"
                                      data-bs-toggle="modal"
                                      data-bs-target="#courseModal"
                                      onClick={resetForm}
                                    >
                                      Add New Course
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        {/* Pagination */}
                        {displayedCourses.length > 0 && (
                          <ReactPaginate
                            previousLabel={
                              <i className="fas fa-chevron-left"></i>
                            }
                            nextLabel={<i className="fas fa-chevron-right"></i>}
                            pageCount={pageCount}
                            onPageChange={({ selected }) =>
                              setCurrentPage(selected)
                            }
                            containerClassName={
                              "pagination justify-content-center mt-4"
                            }
                            pageClassName={"page-item"}
                            pageLinkClassName={"page-link"}
                            previousClassName={"page-item"}
                            previousLinkClassName={"page-link"}
                            nextClassName={"page-item"}
                            nextLinkClassName={"page-link"}
                            activeClassName={"active"}
                            breakLabel={"..."}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            forcePage={currentPage}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Course Modal */}
      <div
        className="modal fade"
        id="courseModal"
        tabIndex="-1"
        aria-labelledby="courseModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header bg-light">
              <h5 className="modal-title" id="courseModalLabel">
                {formData.courseId > 0 ? "Edit Course" : "Create New Course"}
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetForm}
              ></button>
            </div>
            <div className="modal-body p-4">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleFormSubmit();
                }}
              >
                <div className="row decrease_gap g-4">
                  {/* Left Column - Main Course Info */}
                  <div className="col-md-8">
                    {/* Basic Information Card */}
                    <div className="card mb-4">
                      <div className="card-header bg-light py-2">
                        <h6 className="mb-0">Basic Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label
                              htmlFor="courseCode"
                              className="form-label required"
                            >
                              Course Code
                            </label>
                            <input
                              type="text"
                              className={`form-control ${
                                formSubmitted && !formData.courseCode
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="courseCode"
                              name="courseCode"
                              value={formData.courseCode}
                              onChange={handleInputChange}
                              required
                            />
                            {formSubmitted && !formData.courseCode && (
                              <div className="invalid-feedback">
                                Course code is required
                              </div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <label
                              htmlFor="courseName"
                              className="form-label required"
                            >
                              Course Name
                            </label>
                            <input
                              type="text"
                              className={`form-control ${
                                formSubmitted && !formData.courseName
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="courseName"
                              name="courseName"
                              value={formData.courseName}
                              onChange={handleInputChange}
                              required
                            />
                            {formSubmitted && !formData.courseName && (
                              <div className="invalid-feedback">
                                Course name is required
                              </div>
                            )}
                          </div>
                          <div className="col-12">
                            <label
                              htmlFor="courseDescription"
                              className="form-label"
                            >
                              Description
                            </label>
                            <textarea
                              className="form-control"
                              id="courseDescription"
                              name="courseDescription"
                              value={formData.courseDescription}
                              onChange={handleInputChange}
                              rows="3"
                              style={{ minHeight: "100px" }}
                            ></textarea>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Detailed Information Cards */}
                    <div className="accordion" id="courseDetailsAccordion">
                      {/* About Course */}
                      <div className="accordion-item mb-3">
                        <h2 className="accordion-header" id="headingAbout">
                          <button
                            className="accordion-button py-2"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseAbout"
                            aria-expanded="true"
                            aria-controls="collapseAbout"
                          >
                            About This Course
                          </button>
                        </h2>
                        <div
                          id="collapseAbout"
                          className="accordion-collapse collapse show"
                          aria-labelledby="headingAbout"
                          data-bs-parent="#courseDetailsAccordion"
                        >
                          <div className="accordion-body p-0">
                            <div
                              ref={aboutUsRef}
                              id="about-us-editor"
                              style={{ minHeight: "200px" }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      {/* What You'll Learn */}
                      <div className="accordion-item mb-3">
                        <h2 className="accordion-header" id="headingLearn">
                          <button
                            className="accordion-button collapsed py-2"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseLearn"
                            aria-expanded="false"
                            aria-controls="collapseLearn"
                          >
                            What You'll Learn
                          </button>
                        </h2>
                        <div
                          id="collapseLearn"
                          className="accordion-collapse collapse"
                          aria-labelledby="headingLearn"
                          data-bs-parent="#courseDetailsAccordion"
                        >
                          <div className="accordion-body p-0">
                            <div
                              className="ql-editor"
                              ref={whatWillLearnRef}
                              id="what-will-learn-editor"
                              style={{ minHeight: "200px" }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      {/* Course Materials */}
                      <div className="accordion-item">
                        <h2 className="accordion-header" id="headingMaterials">
                          <button
                            className="accordion-button collapsed py-2"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseMaterials"
                            aria-expanded="false"
                            aria-controls="collapseMaterials"
                          >
                            Course Materials
                          </button>
                        </h2>
                        <div
                          id="collapseMaterials"
                          className="accordion-collapse collapse"
                          aria-labelledby="headingMaterials"
                          data-bs-parent="#courseDetailsAccordion"
                        >
                          <div className="accordion-body p-0">
                            <div
                              ref={materialIncludeRef}
                              id="material-include-editor"
                              style={{ minHeight: "200px" }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Settings and Image */}
                  <div className="col-md-4">
                    <div className="d-flex flex-column gap-3">
                      {/* Course Image Card */}
                      <div className="card">
                        <div className="card-header bg-light py-2">
                          <h6 className="mb-0">Course Thumbnail</h6>
                        </div>
                        <div className="card-body">
                          <div
                            className="d-flex justify-content-center mb-3"
                            style={{
                              height: "180px",
                              backgroundColor: "#f8f9fa",
                            }}
                          >
                            {thumbnailUrl ? (
                              <img
                                src={thumbnailUrl}
                                alt="Course thumbnail preview"
                                className="img-fluid rounded h-100"
                                style={{ objectFit: "cover" }}
                              />
                            ) : (
                              <div className="d-flex flex-column align-items-center justify-content-center h-100 w-100 text-muted">
                                <i className="fas fa-image fa-3x mb-2"></i>
                                <span>No image selected</span>
                              </div>
                            )}
                          </div>
                          <div className="d-grid">
                            <input
                              type="file"
                              id="CouseThumbnailImageFile"
                              className="d-none"
                              accept="image/*"
                              onChange={handleImageChange}
                            />
                            <label
                              htmlFor="CouseThumbnailImageFile"
                              className="btn btn-outline-primary"
                            >
                              <i className="fas fa-upload me-2"></i>
                              {thumbnailUrl ? "Change Image" : "Upload Image"}
                            </label>
                          </div>
                          {imageSizeError && (
                            <div className="text-danger small mt-2">
                              {imageSizeError}
                            </div>
                          )}
                          <small className="text-muted d-block mt-2 text-center">
                            Recommended: 800x450px (16:9 ratio), Max 2MB
                          </small>
                        </div>
                      </div>

                      {/* Course Settings Card */}
                      <div className="card">
                        <div className="card-header bg-light py-2">
                          <h6 className="mb-0">Course Settings</h6>
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <label
                              htmlFor="CourseDifficulty"
                              className="form-label"
                            >
                              Difficulty Level
                            </label>
                            <select
                              className="form-select"
                              id="CourseDifficulty"
                              name="CourseDifficulty"
                              value={formData.CourseDifficulty}
                              onChange={handleInputChange}
                            >
                              <option value="Easy">Easy</option>
                              <option value="Medium">Medium</option>
                              <option value="Hard">Hard</option>
                            </select>
                          </div>
                          <div className="mb-3">
                            <label
                              htmlFor="CourseDuration"
                              className="form-label"
                            >
                              Duration (hours)
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              id="CourseDuration"
                              name="CourseDuration"
                              value={formData.CourseDuration}
                              onChange={handleInputChange}
                              min="1"
                            />
                          </div>
                          <div className="mb-3">
                            <label htmlFor="AuthorName" className="form-label">
                              Author Name
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="AuthorName"
                              name="AuthorName"
                              value={formData.AuthorName}
                              onChange={handleInputChange}
                            />
                          </div>
                          {formData.courseId !== 0 && (
                            <div className="mb-3">
                              <label
                                htmlFor="courseStatus"
                                className="form-label"
                              >
                                Course Status
                              </label>
                              <select
                                className="form-select"
                                id="courseStatus"
                                name="courseStatus"
                                value={formData.courseStatus.toString()}
                                onChange={handleInputChange}
                              >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                              </select>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={resetForm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleFormSubmit}
                disabled={!!imageSizeError}
              >
                {formData.courseId > 0 ? "Update Course" : "Create Course"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}